import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaTrash, FaCar, FaBox, FaWeightHanging, FaShippingFast } from 'react-icons/fa';
import { CustomButton10Ca } from '../Templates.js';
import Spinner from '../common/Spinner';
import './MyProfile.css';

const ActivePosts = ({ activePosts, activePackages, onDeletePost, onDeletePackage, isLoading }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const postsPerPage = 3;
  
  // Combine active posts and packages for pagination
  const allActivePosts = [
    ...(activePosts || []).map(post => ({ ...post, isPackage: false })),
    ...(activePackages || []).map(pkg => ({ ...pkg, isPackage: true }))
  ];

  // Sort by creation date (newest first)
  allActivePosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Get paginated posts
  const getPaginatedPosts = () => {
    const startIndex = (page - 1) * postsPerPage;
    const endIndex = startIndex + postsPerPage;
    return allActivePosts.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(allActivePosts.length / postsPerPage);
  const paginatedPosts = getPaginatedPosts();

  // Handle post deletion (archives post)
  const handleDelete = (id, isPackage) => {
    if (isPackage) {
      onDeletePackage(id);
    } else {
      onDeletePost(id);
    }
  };

  // Handle post view
  const handleViewPost = (id, isPackage) => {
    if (isPackage) {
      navigate(`/package/${id}`);
    } else {
      navigate(`/post/${id}`);
    }
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return t('posts.not_specified');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString || t('posts.not_specified');
    }
  };

  const formatAddress = (item) => {
    // Check for newer address format first
    if (item.address_from_city || item.address_from_street) {
      const parts = [item.address_from_street, item.address_from_city, item.address_from_country]
        .filter(Boolean);
      return parts.join(', ') || t('posts.not_specified');
    }
    // Fallback to old format
    return item.address_from || t('posts.not_specified');
  };

  const formatAddressTo = (item) => {
    // Check for newer address format first
    if (item.address_to_city || item.address_to_street) {
      const parts = [item.address_to_street, item.address_to_city, item.address_to_country]
        .filter(Boolean);
      return parts.join(', ') || t('posts.not_specified');
    }
    // Fallback to old format
    return item.address_to || t('posts.not_specified');
  };

  if (isLoading) {
    return (
      <div className="ActivePosts loading">
        <h3>{t('posts.active_posts')}:</h3>
        <Spinner size="medium" />
      </div>
    );
  }

  return (
    <div className="ActivePosts">
      <h3>{t('posts.active_posts')}:</h3>
      {allActivePosts.length > 0 ? (
        <>
          {paginatedPosts.map((item) => (
            <div 
              key={`${item.isPackage ? 'pkg' : 'post'}-${item.id}`} 
              className={`Apost ${item.isPackage ? 'package-post' : ''}`}
              onClick={() => handleViewPost(item.id, item.isPackage)}
            >
              <div className="post-header">
                <span className="post-role">
                  {item.isPackage ? 
                    t('posts.package') : 
                    t(`posts.${item.role.toLowerCase()}`)
                  }
                </span>
                <CustomButton10Ca
                  onClick={(e) => {
                    e.stopPropagation();  // Prevent triggering post view
                    handleDelete(item.id, item.isPackage);
                  }}
                  disabled={isLoading}
                  title={t('posts.archive_post')}
                >
                  {isLoading ? <Spinner size="small" showText={false} thickness={2} /> : <FaTrash size={14} />}
                </CustomButton10Ca>
              </div>
              
              {/* Regular post details */}
              {!item.isPackage && (
                <>
                  {item.car && <div><strong>{t('posts.car')}:</strong> {item.car}</div>}
                  {item.seats && <div><strong>{t('posts.seats')}:</strong> {item.seats}</div>}
                </>
              )}
              
              {/* Package-specific details */}
              {item.isPackage && (
                <>
                  <div className="package-details">
                    <div>
                      <FaBox className="package-icon" />
                      <strong>{t('posts.package_size')}:</strong> {item.package_size}
                    </div>
                    <div>
                      <FaWeightHanging className="package-icon" />
                      <strong>{t('posts.package_weight')}:</strong> {item.package_weight}
                    </div>
                    <div>
                      <FaShippingFast className="package-icon" />
                      <strong>{t('posts.urgency')}:</strong> {item.delivery_urgency}
                    </div>
                  </div>
                </>
              )}
              
              {/* Common details */}
              <div><strong>{t('posts.address_from')}:</strong> {formatAddress(item)}</div>
              <div><strong>{t('posts.address_to')}:</strong> {formatAddressTo(item)}</div>
              
              <div className="post-time-details">
                <div><strong>{t('posts.starting_time')}:</strong> {item.travel_time || t('posts.not_specified')}</div>
                <div><strong>{t('posts.starting_date')}:</strong> {formatDate(item.travel_date)}</div>
              </div>
              
              {!item.isPackage && (
                <div><strong>{t('posts.drive_type')}:</strong> {t(`posts.${item.travel_type.toLowerCase().replace('-', '_')}`)}</div>
              )}
            </div>
          ))}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <button 
                className="pagination-button"
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                <FaChevronLeft />
              </button>
              
              <div className="pagination-info">
                {t('pagination.page')} {page} / {totalPages}
              </div>
              
              <button 
                className="pagination-button"
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="no-posts-message">{t('posts.no_active')}</p>
      )}
    </div>
  );
};

export default ActivePosts;